import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsUpDownIcon } from '@heroicons/react/20/solid'
import Decimal from 'decimal.js'
import { REFERRER_KEY, SOL_MINT, USDC_MINT, USDT_MINT } from 'utils/constants'
import { useWallet } from '@solana/wallet-adapter-react'
import { JupiterV6RouteInfo } from 'types/jupiter'
import useQuoteRoutes from './useQuoteRoutes'
import store from 'stores/store'
import { floorToDecimal } from 'utils/numbers'
import {
  Connection,
  PublicKey,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import {
  sendTxAndConfirm,
  TransactionError,
  tryStringify,
} from 'utils/governance/tools'
import { notify } from 'utils/notifications'
import Loading from '@components/loading/Loading'
import {
  useInvalidateWalletTokens,
  useWalletTokens,
} from 'hooks/useWalletTokens'
import { usePlausibleChecked } from 'hooks/usePlausibleChecked'
import SellInput from './SellInput'
import BuyInput from './BuyInput'
import SwapInfo from './SwapInfo'
import { sleep } from 'utils'
import InlineNotification from '@components/shared/InlineNotification'
import { useReferrerVault } from 'hooks/useReferrerVault'
import { enchantVtxWithFees } from 'utils/referrals'
import { useQuery } from '@tanstack/react-query'
import { useSolPrice } from 'hooks/useSolPrice'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { calcFeeParams } from 'utils/fee'
import ConnectButton from '@components/shared/ConnectButton'
import { TOKEN_PROGRAM_ID } from '@solana/spl-token'
import { getFeeEstimate } from 'utils/priorityFee'
import { isError } from 'lodash'
import { fetchLastPriceForMints, walletBalanceForToken } from 'utils/tokens'
import { validateTradeForm } from 'utils/trade'
import { format, milliseconds } from 'date-fns'
import { useTranslation } from 'next-i18next'
import { toContextTuplesString } from 'utils/logging'
import { makePythClient } from 'utils/makePythClient'
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes'
import RouterDisplay from './RouterDisplay'
import type { InternResponse, SwapData } from 'pages/api/intern/swap'
import clsx from 'clsx'

const nonDegenMints = [SOL_MINT, USDC_MINT, USDT_MINT]

const set = store.getState().set

interface CommonCreateSwapTransactionResponse {
  computeUnitLimit: number
  lastValidBlockHeight: number
  prioritizationFeeLamports: number
  prioritizationType: {
    computeBudget: {
      microLamports: number
      estimatedMicroLamports: number | null
    }
  } | null
  swapTransaction: string

  [key: string]: unknown
}

/**
 * Given a route, fetch the transaction for the user to sign.
 * *This function should ONLY be used for wallet swaps*
 */
export const fetchWalletSwapTransaction = async (
  selectedRoute: JupiterV6RouteInfo,
  userPublicKey: PublicKey,
  slippage: number,
  connection: Connection,
  origin: 'dflow',
): Promise<VersionedTransaction> => {
  const params: {
    quoteResponse: JupiterV6RouteInfo
    userPublicKey: PublicKey
    slippageBps: number
    computeUnitPriceMicroLamports?: number
  } = {
    quoteResponse: selectedRoute,
    userPublicKey,
    slippageBps: Math.ceil(slippage * 100),
  }
  const txFee = await getFeeEstimate(connection)
  params.computeUnitPriceMicroLamports = txFee

  const createSwapTransactionResponse = await fetch('/api/dflow', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })

  if (!createSwapTransactionResponse.ok) {
    console.error(
      `Error fetching swap transaction: ${toContextTuplesString({
        status: createSwapTransactionResponse.status,
        response: await createSwapTransactionResponse.text(),
        origin,
        slippage,
      })}`,
    )
    throw new Error(
      `Failed to create swap transaction: origin=${origin}, status=${createSwapTransactionResponse.status}`,
    )
  }
  const { swapTransaction } =
    (await createSwapTransactionResponse.json()) as CommonCreateSwapTransactionResponse

  return VersionedTransaction.deserialize(
    Uint8Array.from(Buffer.from(swapTransaction, 'base64')),
  )
}

const SWAP_ROUTE_REFETCH_INTERVAL = milliseconds({ seconds: 20 })

const SwapForm = () => {
  const { t } = useTranslation(['swap', 'messages'])
  // initial state is undefined null is returned on error
  const [selectedRoute, setSelectedRoute] =
    useState<JupiterV6RouteInfo | null>()
  const [submitting, setSubmitting] = useState(false)
  const [animateSwitchArrow, setAnimateSwitchArrow] = useState(0)
  const [error, setError] = useState('')
  const { connected, publicKey, signAllTransactions } = useWallet()
  const wallet = useWallet()
  const plausible = usePlausibleChecked()
  const { solPrice } = useSolPrice()

  const slippage = store((s) => s.swap.slippage)
  const amountInFormValue = store((s) => s.swap.amountIn)
  const inputToken = store((s) => s.swap.inputToken)
  const outputToken = store((s) => s.swap.outputToken)
  const invalidateWalletTokens = useInvalidateWalletTokens()
  const [localReferrer] = useLocalStorage(REFERRER_KEY)

  const {
    bestRoute,
    isFetching: fetchingRoute,
    refetch: refetchRoute,
  } = useQuoteRoutes({
    inputMint: inputToken.mint,
    inputMintProgram: inputToken.token_program,
    outputMint: outputToken.mint,
    outputMintProgram: outputToken.token_program,
    nativeAmount: amountInFormValue
      ? new Decimal(amountInFormValue)
          .mul(10 ** (inputToken.decimals ?? 6))
          .round()
      : new Decimal(0),
    slippage,
    swapMode: 'ExactIn',
    refetchIntervalMs: SWAP_ROUTE_REFETCH_INTERVAL,
    enabled: !!(
      inputToken.mint &&
      outputToken.mint &&
      amountInFormValue &&
      inputToken
    ),
  })

  const { data: lastPriceForOutputToken } = useQuery({
    queryKey: ['swap-output-token-last-price', outputToken.mint],
    queryFn: async () => {
      const lastPrice = await fetchLastPriceForMints([outputToken.mint])
      return lastPrice?.[0]?.price ?? 0
    },
    enabled: !outputToken.latest_price_usd,
    refetchOnWindowFocus: false,
    staleTime: milliseconds({ seconds: 10 }),
    cacheTime: milliseconds({ seconds: 60 }),
  })

  const feeParams = useMemo(() => {
    let lastPriceUsd = lastPriceForOutputToken ?? outputToken.latest_price_usd

    if (
      !lastPriceUsd &&
      bestRoute &&
      bestRoute.outAmount &&
      'swapUsdValue' in bestRoute &&
      typeof bestRoute.swapUsdValue === 'string'
    ) {
      lastPriceUsd =
        Number(bestRoute.swapUsdValue) /
        (bestRoute.outAmount / (outputToken.decimals ?? 6))
    }
    return calcFeeParams(
      bestRoute?.outAmount?.toString() || '0',
      !!localReferrer?.wallet,
      outputToken.decimals ?? 6,
      lastPriceUsd,
      solPrice,
      outputToken.mint,
      outputToken.token_program || TOKEN_PROGRAM_ID.toBase58(),
      inputToken.mint,
    )
  }, [
    bestRoute,
    inputToken.mint,
    lastPriceForOutputToken,
    localReferrer?.wallet,
    outputToken.decimals,
    outputToken.latest_price_usd,
    outputToken.mint,
    outputToken.token_program,
    solPrice,
  ])

  const { vault: referrerVault } = useReferrerVault(
    feeParams?.feeMint.toBase58(),
    feeParams?.feeProgram.toBase58(),
  )

  const { walletTokens } = useWalletTokens()

  const amountInAsDecimal: Decimal = useMemo(() => {
    return Number(amountInFormValue)
      ? new Decimal(amountInFormValue)
      : new Decimal(0)
  }, [amountInFormValue])

  const outAmount = useMemo(() => {
    if (!selectedRoute || !outputToken.decimals || !feeParams)
      return new Decimal(0)
    const decimals = outputToken.decimals
    const rawOutAmount = feeParams.outAmount / 10 ** decimals
    return floorToDecimal(rawOutAmount, decimals)
  }, [selectedRoute, outputToken, feeParams])

  const [walletMax, sellDecimals] = useMemo(() => {
    if (!inputToken.mint) return ['0', 6]
    const walletBalance = walletBalanceForToken(walletTokens, inputToken.mint)
    const max = floorToDecimal(
      walletBalance.balance,
      walletBalance.decimals,
    ).toFixed()
    return [max, walletBalance.decimals]
  }, [walletTokens, inputToken.mint])

  const processMsg = useCallback(
    async (publicKey: PublicKey, volume: number, outputTokenMint: string) => {
      const fallbackMessage = t('messages:transaction-confirmed')
      try {
        const now = new Date()
        const resp = await fetch('/api/intern/swap', {
          method: 'POST',
          body: JSON.stringify({
            wallet: publicKey.toString(),
            volume: volume,
            timestamp: now.getTime(),
            timezoneOffset: format(now, 'xxx'),
            isReferred: Boolean(localReferrer),
            isFirstTime: false,
            isDegen: !nonDegenMints.includes(outputTokenMint),
          } satisfies SwapData),
          headers: {
            'Content-Type': 'application/json',
          },
        })
        if (!resp.ok) {
          console.error(
            `Error processing confirmation msg: ${toContextTuplesString({
              status: resp.status,
              response: await resp.text(),
            })}`,
          )
          return fallbackMessage
        }
        const response = (await resp.json()) as InternResponse
        return response.message
      } catch (e) {
        console.error('Error processing confirmation msg', e)
        return fallbackMessage
      }
    },
    [localReferrer, t],
  )

  const onWalletSwap = useCallback(async () => {
    if (
      !selectedRoute ||
      !inputToken.mint ||
      !outputToken.mint ||
      !publicKey ||
      !signAllTransactions ||
      !wallet ||
      !feeParams
    )
      return

    const getSwapTxes = async () => {
      const txFromRoute = selectedRoute.tx

      if (txFromRoute && selectedRoute.origin === 'okx') {
        txFromRoute.message.recentBlockhash = (
          await connection.getLatestBlockhash('finalized')
        ).blockhash
      }

      const vtx =
        txFromRoute ??
        (selectedRoute.origin === 'dflow'
          ? await fetchWalletSwapTransaction(
              selectedRoute,
              publicKey,
              slippage,
              connection,
              selectedRoute.origin,
            )
          : null)

      if (!vtx) {
        throw new Error(
          `Failed to get swap transaction: no transaction for route: origin=${selectedRoute.origin}`,
        )
      }

      return enchantVtxWithFees(
        vtx,
        publicKey,
        referrerVault,
        connection,
        feeParams.fee || 0,
        feeParams.feeMint || PublicKey.default,
        feeParams.feeProgram,
        bestRoute?.origin === 'pyth',
      )
    }

    const hasError = validateTradeForm(amountInAsDecimal, walletMax)
    if (hasError) {
      setError(hasError)
      return
    }
    const connection = store.getState().connection
    setSubmitting(true)
    let swapTxId: string | undefined
    let feeTxId: string | undefined

    try {
      const txes = await getSwapTxes()
      plausible('SwapTransactionStart')
      const latestBlockhash = await connection.getLatestBlockhash('finalized')
      const signed = await signAllTransactions(txes)
      // if tx is too big then we need to split it
      // we need to be sure swap happened to take the fee
      // that's why we don't use promise.all with map
      try {
        if (bestRoute?.referenceId) {
          const client = makePythClient()
          const accountPosition = signed[0].message.staticAccountKeys.findIndex(
            (key) => key.equals(publicKey),
          )
          const signature = signed[0].signatures[accountPosition]
          const submitQuote = {
            chainId: 'solana',
            referenceId: bestRoute?.referenceId,
            userSignature: bs58.encode(signature),
          }
          swapTxId = bs58.encode(signed[0].signatures[0]).toString()
          await client.submitQuote(submitQuote)
        } else {
          swapTxId = await sendTxAndConfirm(
            connection,
            signed[0],
            latestBlockhash,
            {
              transactionSent: t('messages:transaction-sent'),
              waitingForConfirmation: t('messages:waiting-for-confirmation'),
            },
          )
        }
        set((s) => {
          s.swap.amountIn = ''
        })
        const msg = await processMsg(
          publicKey,
          outAmount.toNumber() *
            (lastPriceForOutputToken || outputToken.latest_price_usd || 0),
          outputToken.mint,
        )
        notify({
          title: msg,
          type: 'success',
          txid: swapTxId,
          description: `Swapped ${amountInAsDecimal} ${
            inputToken.symbol
          } for ${outAmount.toString()} ${outputToken.symbol}`,
        })
        plausible('SwapTransactionSuccess', {
          props: {
            tokenSymbol: outputToken.symbol,
            tokenMint: outputToken.mint,
            // description: `${publicKey.toString()} swapped ${amountInAsDecimal} ${inputTokenInfo?.symbol} for ${minimumReceived} ${outputTokenInfo?.symbol} ${
            //   minTradeValue ? minTradeValue : 'value unknown'
            // }`,
            wallet: publicKey.toString(),
            txid: swapTxId,
            routerName: selectedRoute.origin || 'unknown',
          },
        })
      } catch (e) {
        console.error(
          `Error during swap (swap transaction): ${toContextTuplesString({
            inputToken: inputToken.mint,
            outputToken: outputToken.mint,
            swapRouteOrigin: selectedRoute.origin || 'unknown',
            swapTxId:
              swapTxId ?? (e instanceof TransactionError ? e.txid : 'unknown'),
            transactionsCount: signed.length,
          })}`,
          e,
        )
        notify({
          title: t('messages:errors.transaction-error'),
          description:
            e != null
              ? e instanceof TransactionError || isError(e)
                ? transformErrorMsg(e.message, t('error.adjust-slippage'))
                : String(e)
              : t('messages:errors.general-try-again'),
          type: 'error',
          txid: swapTxId ?? (e instanceof TransactionError ? e.txid : ''),
        })
        plausible('SwapTransactionError', {
          props: {
            tokenSymbol: outputToken.symbol,
            tokenMint: outputToken.mint,
            // description: `${publicKey.toString()} swapped ${amountInAsDecimal} ${inputTokenInfo?.symbol} for ${minimumReceived} ${outputTokenInfo?.symbol} ${
            //   minTradeValue ? minTradeValue : 'value unknown'
            // }`,
            wallet: publicKey.toString(),
            txid: swapTxId ?? (e instanceof TransactionError ? e.txid : ''),
          },
        })
        return
      }

      try {
        if (signed.length > 1) {
          feeTxId = await sendTxAndConfirm(
            connection,
            signed[1],
            latestBlockhash,
            false,
          )
          plausible('SwapTransactionFeeSuccess', {
            props: {
              tokenSymbol: outputToken.symbol,
              tokenMint: outputToken.mint,
              // description: `${publicKey.toString()} swapped ${amountInAsDecimal} ${inputTokenInfo?.symbol} for ${minimumReceived} ${outputTokenInfo?.symbol} ${
              //   minTradeValue ? minTradeValue : 'value unknown'
              // }`,
              wallet: publicKey.toString(),
              txid: feeTxId,
              routerName: selectedRoute.origin || 'unknown',
            },
          })
        }
      } catch (e) {
        console.error(
          `Error during swap (fee transaction): ${toContextTuplesString({
            inputToken: inputToken.mint,
            outputToken: outputToken.mint,
            swapRouteOrigin: selectedRoute.origin || 'unknown',
            swapTxId,
            feeTxId:
              feeTxId ?? (e instanceof TransactionError ? e.txid : 'unknown'),
          })}`,
          e,
        )
        plausible('SwapTransactionFeeError', {
          props: {
            tokenSymbol: outputToken.symbol,
            tokenMint: outputToken.mint,
            // description: `${publicKey.toString()} swapped ${amountInAsDecimal} ${inputTokenInfo?.symbol} for ${minimumReceived} ${outputTokenInfo?.symbol} ${
            //   minTradeValue ? minTradeValue : 'value unknown'
            // }`,
            wallet: publicKey.toString(),
            txid: feeTxId
              ? feeTxId
              : e instanceof TransactionError
                ? e.txid
                : '',
          },
        })
        return
      }
    } catch (e) {
      console.error('Error during swap', e)
      if (`${e}`.toLocaleLowerCase().includes('user rejected')) {
        return
      }
      notify({
        title: t('messages:errors.transaction-error'),
        description:
          e != null
            ? e instanceof TransactionError || isError(e)
              ? transformErrorMsg(e.message, t('error.adjust-slippage'))
              : String(e)
            : t('messages:errors.general-try-again'),
        type: 'error',
        txid: swapTxId ?? (e instanceof TransactionError ? e.txid : ''),
      })
    } finally {
      setSubmitting(false)
      await sleep(1000)
      void invalidateWalletTokens()
    }
  }, [
    selectedRoute,
    inputToken.mint,
    inputToken.symbol,
    outputToken,
    publicKey,
    signAllTransactions,
    wallet,
    feeParams,
    amountInAsDecimal,
    walletMax,
    slippage,
    referrerVault,
    bestRoute?.origin,
    bestRoute?.referenceId,
    plausible,
    outAmount,
    processMsg,
    lastPriceForOutputToken,
    t,
    invalidateWalletTokens,
  ])

  // const setAmountOutFormValue = useCallback((amountOut: string) => {
  //   set((s) => {
  //     s.swap.amountOut = amountOut
  //     if (!parseFloat(amountOut)) {
  //       s.swap.amountIn = ''
  //     }
  //   })
  // }, [])

  useEffect(() => {
    if (typeof bestRoute !== 'undefined') {
      setSelectedRoute(bestRoute)

      // if (buyMint && bestRoute?.outAmount) {
      //   const decimals = outputTokenInfo?.decimals || 6
      //   const outAmount = new Decimal(bestRoute.outAmount)
      //     .div(10 ** decimals)
      //     .toString()
      //   setAmountOutFormValue(outAmount)
      // }
    }
  }, [bestRoute])
  // remove the dependency on outputToken (not sure why it's needed)

  const loadingExactIn: boolean = useMemo(() => {
    return (
      (!!(amountInAsDecimal.toNumber() || outAmount.toNumber()) &&
        connected &&
        selectedRoute == null) ||
      !!(amountInAsDecimal.toNumber() && !outAmount.toNumber())
    )
  }, [amountInAsDecimal, outAmount, connected, selectedRoute])

  const handleSwitchTokens = useCallback(() => {
    const inputToken = store.getState().swap.inputToken
    const outputToken = store.getState().swap.outputToken

    set((state) => {
      state.swap.inputToken = outputToken
      state.swap.outputToken = inputToken
      state.swap.amountIn = ''
    })

    setSelectedRoute(null)
    setAnimateSwitchArrow(
      (prevAnimateSwitchArrow) => prevAnimateSwitchArrow + 1,
    )
  }, [])

  const loading = loadingExactIn || fetchingRoute

  const disabled =
    !selectedRoute || !!selectedRoute.error || submitting || !amountInFormValue

  return (
    <>
      <div className="px-2">
        <div>
          <div className="relative rounded-xl">
            <div className="rounded-t-xl bg-th-bkg-1 p-4">
              <SellInput
                max={walletMax}
                decimals={sellDecimals}
                error={error}
                setError={setError}
              />
            </div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <button
                className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-th-active bg-th-bkg-1 md:hover:bg-th-bkg-3"
                onClick={handleSwitchTokens}
              >
                <ArrowsUpDownIcon
                  className={clsx(
                    'size-5 text-th-active',
                    animateSwitchArrow % 2 == 0 ? 'rotate-0' : 'rotate-180',
                  )}
                />
              </button>
            </div>
            <div className="rounded-b-xl bg-th-bkg-2 p-4">
              <BuyInput
                outAmount={outAmount.toNumber()}
                loading={loadingExactIn}
              />
            </div>
          </div>
          <div className="px-4">
            {connected ? (
              <button
                className="mt-6 flex h-12 w-full items-center justify-center rounded-full bg-th-button font-bold text-th-button-text focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 md:hover:bg-th-button-hover"
                disabled={disabled}
                onClick={onWalletSwap}
              >
                {loading || fetchingRoute ? <Loading /> : t('swap')}
              </button>
            ) : (
              <ConnectButton
                className="mt-4 flex w-full items-center justify-center"
                isLarge
                isSecondary
              />
            )}
            {error ? (
              <div className="flex justify-center pt-2">
                <InlineNotification
                  type="error"
                  desc={error}
                  hideBorder
                  hidePadding
                />
              </div>
            ) : (
              <SwapInfo
                amountInAsDecimal={amountInAsDecimal}
                loading={loading}
                selectedRoute={selectedRoute}
                refetchRouteIntervalMs={SWAP_ROUTE_REFETCH_INTERVAL}
                refetchRoute={refetchRoute}
                fetchingRoute={fetchingRoute}
              />
            )}
          </div>
        </div>
        <div className="mt-6 rounded-lg bg-th-bkg-2 px-4 py-2">
          <RouterDisplay
            selectedRouter={
              outputToken.decimals > 0 && amountInAsDecimal.gt(0)
                ? selectedRoute?.origin
                : undefined
            }
            fetchingRoute={fetchingRoute}
          />
        </div>
      </div>
    </>
  )
}

export default SwapForm

const transformErrorMsg = (val: string, adjustSlippageMsg: string) => {
  if (
    val.toLowerCase().includes('Slippage') ||
    val.toLowerCase().includes('6001') ||
    val.toLocaleLowerCase().includes('15001')
  ) {
    return adjustSlippageMsg
  } else {
    return val
  }
}
